// scanCoverage.js — shared break-scan coverage math, extracted from RollCall.js
// (Scrib Doon) so the Narcs'r'us weekly view computes the SAME percentages the
// Scrib Doon tab shows, without duplicating the logic divergently.
//
// computeScanCoverage({ onsite, roster, scans }, shift) -> per-column coverage:
//   { sosScan:{have,denom,pct}, break1Scan:{...}, lunchScan:{...}, break2Scan:{...} }
// pct = have/denom (0..1). Mirrors RollCall's `coverage` exactly: nearest-time
// scan bucketing, present-for-shift filtering, and the per-break eligibility
// gate (Break 1 needs SOS scan/punch; Break 2 needs Lunch scan/punch).
//
// Inputs are the raw cache payloads:
//   onsite = ONSITE_<site>_<date>/<shift>   -> { onsite:[ {empId,name,...} ] }
//   roster = ROSTER_<site>/data             -> { bbToPerson, empToBb }
//   scans  = ROLLCALL_<date>/<shift>.scans  -> [ {bb,ts,col} ]

const SHIFT_TARGETS = {
  day:   { sos: 8 * 60, break1: 10 * 60 + 15, lunch: 13 * 60 + 15, break2: 16 * 60 + 15 },
  night: { sos: 19 * 60, break1: 21 * 60 + 15, lunch: 24 * 60 + 15, break2: 27 * 60 + 15 },
};
const PUNCH_GATES = {
  day:   { sos: [6 * 60 + 30, 9 * 60],         lunch: [13 * 60, 14 * 60] },
  night: { sos: [18 * 60, 20 * 60],            lunch: [23 * 60 + 30, 25 * 60] },
};
const SHIFT_WINDOW = {
  day:   [6 * 60 + 30, 18 * 60],
  night: [18 * 60, 30 * 60 + 30],
};
export const SCAN_COLS = ['sosScan', 'break1Scan', 'lunchScan', 'break2Scan'];
export const SCAN_COL_LABELS = { sosScan: 'SOS', break1Scan: '1st Break', lunchScan: 'Lunch', break2Scan: '2nd Break' };

const inGate = (min, gate) => min != null && gate && min >= gate[0] && min <= gate[1];

function hhmmToMin(hhmm, shift) {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  let mins = h * 60 + m;
  if (shift === 'night' && mins < 6 * 60) mins += 1440;
  return mins;
}
function tsToMinPT(ts, shift) {
  const d = new Date(ts);
  if (isNaN(d)) return null;
  const pt = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  let mins = pt.getHours() * 60 + pt.getMinutes();
  if (shift === 'night' && mins < 6 * 60) mins += 1440;
  return mins;
}
function nearestScanCol(scanMin, shift) {
  const t = SHIFT_TARGETS[shift];
  const opts = [['sosScan', t.sos], ['break1Scan', t.break1], ['lunchScan', t.lunch], ['break2Scan', t.break2]];
  let best = opts[0][0], bestD = Math.abs(scanMin - opts[0][1]);
  for (const [k, v] of opts) {
    const d = Math.abs(scanMin - v);
    if (d < bestD) { bestD = d; best = k; }
  }
  return best;
}
function presentForShift(o, shift, hasScan) {
  if (hasScan) return true;
  if (o && o.currentlyIn === true) return true;
  const win = SHIFT_WINDOW[shift];
  const list = Array.isArray(o && o.punchList) ? o.punchList : [];
  for (const p of list) {
    const m = hhmmToMin(p, shift);
    if (m != null && m >= win[0] && m <= win[1]) return true;
  }
  return false;
}

export function computeScanCoverage({ onsite, roster, scans }, shift) {
  const bbToPerson = (roster && roster.bbToPerson) || {};
  const empToBb = (roster && roster.empToBb) || {};
  const rosterLoaded = Object.keys(bbToPerson).length > 0;
  const fcaEmpIds = new Set(Object.keys(empToBb));
  const onsiteAll = (onsite && onsite.onsite) || [];
  const onsiteList = rosterLoaded ? onsiteAll.filter(o => fcaEmpIds.has(o.empId)) : onsiteAll;
  const targets = SHIFT_TARGETS[shift];
  const allScans = Array.isArray(scans) ? scans : [];

  // empId -> { col: latestTs } via BB->person.
  const scanByEmp = {};
  allScans.forEach(s => {
    const person = bbToPerson[s.bb];
    if (!person) return;
    const col = s.col || nearestScanCol(tsToMinPT(s.ts, shift) ?? 0, shift);
    const e = person.empId;
    if (!scanByEmp[e]) scanByEmp[e] = {};
    const cur = scanByEmp[e][col];
    if (!cur || s.ts > cur) scanByEmp[e][col] = s.ts;
  });

  const rows = onsiteList.map(o => {
    const mk = (hhmm) => hhmm ? { hhmm, min: hhmmToMin(hhmm, shift) } : null;
    let sosPunch = mk(o.sosPunch);
    let lunchPunch = mk(o.lunchIn);
    const gates = PUNCH_GATES[shift];
    if (sosPunch && !inGate(sosPunch.min, gates.sos)) sosPunch = null;
    if (lunchPunch && !inGate(lunchPunch.min, gates.lunch)) lunchPunch = null;
    const sc = scanByEmp[o.empId] || {};
    const hasAnyScan = !!(sc.sosScan || sc.break1Scan || sc.lunchScan || sc.break2Scan);
    return {
      present: presentForShift(o, shift, hasAnyScan),
      cells: {
        sosPunch: sosPunch ? sosPunch.hhmm : '',
        sosScan: sc.sosScan || '',
        break1Scan: sc.break1Scan || '',
        lunchPunch: lunchPunch ? lunchPunch.hhmm : '',
        lunchScan: sc.lunchScan || '',
        break2Scan: sc.break2Scan || '',
      },
    };
  }).filter(r => r.present);

  const eligibleFor = (k, r) => {
    if (k === 'break1Scan') return !!(r.cells.sosScan || r.cells.sosPunch);
    if (k === 'break2Scan') return !!(r.cells.lunchScan || r.cells.lunchPunch);
    return true;
  };
  const coverage = {};
  SCAN_COLS.forEach(k => {
    const eligible = rows.filter(r => eligibleFor(k, r));
    const denom = eligible.length;
    const have = eligible.filter(r => r.cells[k]).length;
    coverage[k] = { have, denom, pct: denom ? have / denom : null };
  });
  return coverage;
}
